import { Layer, ObjectCategory } from "@common/constants";
import { FloorNames } from "@common/utils/terrain";
import { Container, Sprite, Texture } from "pixi.js";
import { Game } from "../game";
import { CameraManager } from "../managers/cameraManager";
import { MapManager } from "../managers/mapManager";
import { PIXI_SCALE } from "../utils/constants";
import { TrenchioSprite } from "../utils/pixi";
import { YetiPrints } from "@common/definitions/animals";
import { roofOver, underCover } from "../utils/roofs";
import { warAtlas } from "./warDecals";
import type { WarWeatherState } from "./warLook";

interface Streak { sprite: Sprite, vx: number, vy: number }
/**
 * a rain splash and a footprint (0.98.8: one not shown is kept at no alpha, not hidden: a sprite hidden or shown has Pixi
 * build the world's whole draw list again, and in the rain or on mud that was most frames; `shown` and `on` are what
 * `visible` was)
 */
interface Splash { sprite: Sprite, age: number, life: number, size: number, shown: boolean }
interface Print { sprite: Sprite, age: number, on: boolean }
interface Walker { x: number, y: number, side: number }
/** a snowflake: drifting down with the wind, swaying, nearer ones bigger and sliding faster as the camera moves */
interface Flake { sprite: Sprite, vx: number, vy: number, phase: number, sway: number, depth: number }

/**
 * a snowflake's picture, made once: a soft white dot with a faint grey-blue rim (white alone was lost on the snow it
 * falls over)
 */
let flakeTexture: Texture | undefined;
const getFlakeTexture = (): Texture => {
    if (flakeTexture) return flakeTexture;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 24;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        const g = ctx.createRadialGradient(12, 12, 0, 12, 12, 12);
        g.addColorStop(0, "rgba(255, 255, 255, 1)");
        g.addColorStop(0.34, "rgba(255, 255, 255, 0.96)");
        g.addColorStop(0.56, "rgba(232, 238, 247, 0.62)");
        g.addColorStop(0.74, "rgba(128, 144, 172, 0.3)");
        g.addColorStop(1, "rgba(128, 144, 172, 0)");
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, 24, 24);
    }
    return flakeTexture = Texture.from(canvas);
};

class WarWeatherClass {
    private _rainLayer?: Container;
    private _streaks: Streak[] = [];
    private _flakes: Flake[] = [];
    /** the boot prints' darkness (deeper in snow) */
    private _printAlpha = 0.46;
    /** a thunderstorm's splashes on water open wide rings (1.05) */
    private _storm = false;
    private _time = 0;
    private _groundLayer?: Container;
    private _splashes: Splash[] = [];
    private _prints: Print[] = [];
    /** the Yeti's prints (0.98.4): great, deep, five-toed, in the snow where it walks (`bigPrint`) */
    private _bigPrints: Print[] = [];
    private _walkers = new Map<number, Walker>();
    private _printLife = 10;
    private _printMax = 0;
    private _rain = 0;
    private _splashRate = 0;
    private _splashTimer = 0;
    private _lastOrigin?: { x: number, y: number };
    private _screen = { w: 1, h: 1 };
    /**
     * how far the falling rain and snow are faded out, the player under a roof or under the ground (0 none, 1 all), and
     * when that was last looked at (it fell through the attic's dark, in every house and bunker: 1.06.1's audit)
     */
    private _cover = 0;
    private _covered = false;
    private _coverAt = 0;

    clear(): void {
        this._rainLayer?.destroy({ children: true }); this._rainLayer = undefined;
        this._groundLayer?.destroy({ children: true }); this._groundLayer = undefined;
        this._streaks = []; this._splashes = []; this._prints = []; this._bigPrints = []; this._flakes = [];
        this._walkers.clear();
        this._lastOrigin = undefined;
    }

    /** between matches: drop footprints and splashes but keep the layers */
    clearTransient(): void {
        for (const p of this._prints) { p.on = false; p.sprite.alpha = 0; }
        for (const p of this._bigPrints) { p.on = false; p.sprite.alpha = 0; }
        for (const s of this._splashes) { s.shown = false; s.sprite.alpha = 0; }
        this._walkers.clear();
    }

    setup(quality: string, weather: WarWeatherState, root: Container, fx?: Sprite): void {
        this.clear();
        const { frame } = warAtlas();
        const rich = quality === "high" || quality === "medium";
        const snow = weather.snow > 0;
        this._storm = weather.storm;
        // (a thunderstorm's rain shows at Low too, lighter: 1.05)
        this._rain = rich ? weather.rain : weather.storm ? weather.rain * 0.5 : 0;
        // only the last 3 seconds of footsteps are shown (keeps the sprite count low); in snow they stay 40 seconds, the
        // oldest giving way to the newest (1.05: boot prints that stay), at Low too
        this._printMax = snow
            ? quality === "high" ? 600 : quality === "medium" ? 380 : 220
            : quality === "high" ? 110 : quality === "medium" ? 70 : 0;
        this._printLife = snow ? 40 : 3;
        this._printAlpha = snow ? 0.34 : 0.46;

        // world-space ground layer: footprints and rain splashes, above the decals, below everything else
        const ground = this._groundLayer = new Container();
        ground.zIndex = -3;
        CameraManager.addObject(ground);
        for (let i = 0; i < this._printMax; i++) {
            const sprite = new Sprite(frame("footprint"));
            sprite.anchor.set(0.5); sprite.alpha = 0;
            sprite.scale.set((0.5 * PIXI_SCALE) / 32);
            ground.addChild(sprite);
            this._prints.push({ sprite, age: 99, on: false });
        }

        const screenLayer = (): Container => {
            if (this._rainLayer) return this._rainLayer;
            const layer = this._rainLayer = new Container();
            const index = fx ? root.getChildIndex(fx) + 1 : root.getChildIndex(CameraManager.container) + 1;
            root.addChildAt(layer, index);
            return layer;
        };
        if (this._rain > 0) {
            // (a thunderstorm's: half as many again, longer and faster, slanted harder by its wind)
            const heavy = weather.storm ? 1.5 : 1;
            const count = Math.round((quality === "high" ? 320 : quality === "medium" ? 170 : 110) * this._rain * heavy);
            this._splashRate = (quality === "high" ? 42 : quality === "medium" ? 22 : 14) * this._rain * (weather.storm ? 1.8 : 1);
            const layer = screenLayer();
            const tilt = Math.atan2(weather.wind.x * (weather.storm ? 0.4 : 0.25), 1);
            for (let i = 0; i < count; i++) {
                const sprite = new Sprite(frame("rain"));
                sprite.anchor.set(0.5);
                const len = (16 + Math.random() * 22) * (weather.storm ? 1.3 : 1);
                sprite.scale.set(0.5 + Math.random() * 0.4, len / 64);
                sprite.rotation = -tilt;
                sprite.alpha = (0.1 + Math.random() * 0.2) * (weather.storm ? 1.25 : 1);
                sprite.tint = 0xc9cfc2;
                sprite.position.set(Math.random() * this._screen.w, Math.random() * this._screen.h);
                layer.addChild(sprite);
                const speed = (950 + Math.random() * 500) * (weather.storm ? 1.25 : 1);
                this._streaks.push({ sprite, vx: Math.sin(tilt) * speed, vy: Math.cos(tilt) * speed });
            }
            for (let i = 0; i < (weather.storm ? 110 : 64); i++) {
                const sprite = new Sprite(frame("splash"));
                sprite.anchor.set(0.5); sprite.alpha = 0; sprite.tint = 0xb9c0b0;
                ground.addChild(sprite);
                this._splashes.push({ sprite, age: 1, life: 0.35, size: 1, shown: false });
            }
        }
        // snow (1.05): flakes drifting down with the wind, at every level the look has (fewer at Low)
        if (snow) {
            const layer = screenLayer();
            const count = Math.round((quality === "high" ? 420 : quality === "medium" ? 260 : 140) * weather.snow);
            const texture = getFlakeTexture();
            for (let i = 0; i < count; i++) {
                const sprite = new Sprite(texture);
                sprite.anchor.set(0.5);
                const depth = Math.random();
                // (from about 6 pixels across, far, to 18, near)
                sprite.scale.set((0.26 + depth * 0.5) * (0.85 + Math.random() * 0.3));
                sprite.alpha = 0.55 + depth * 0.4;
                sprite.position.set(Math.random() * this._screen.w, Math.random() * this._screen.h);
                layer.addChild(sprite);
                const fall = 38 + depth * 80 + Math.random() * 20;
                this._flakes.push({ sprite, vx: weather.wind.x * (18 + depth * 30), vy: fall, phase: Math.random() * 6.28, sway: 10 + Math.random() * 22, depth });
            }
        }
    }

    resize(width: number, height: number): void {
        const old = this._screen;
        this._screen = { w: width, h: height };
        if (old.w === width && old.h === height) return;
        // (spread over the new size: the snow kept to the old one's top left until each flake fell off its bottom, the
        // window made bigger mid-match: 1.06.1's audit)
        for (const flake of this._flakes) flake.sprite.position.set(Math.random() * width, Math.random() * height);
        for (const streak of this._streaks) streak.sprite.position.set(Math.random() * width, Math.random() * height);
    }

    /** the player on screen is under a roof or under the ground (not up in the air), looked at a few times a second */
    private _coveredNow(): boolean {
        const now = performance.now();
        if (now < this._coverAt) return this._covered;
        this._coverAt = now + 200;
        const player = Game.activePlayer;
        this._covered = player !== undefined && !player.air && underCover(player.layer, player.position);
        return this._covered;
    }

    update(dt: number, originX: number, originY: number, unitsPerPx: number): void {
        const { w, h } = this._screen;
        this._time += dt;
        // under a roof or the ground, the falling rain and snow fade out (and back in outside)
        if (this._rainLayer) {
            this._cover += ((this._coveredNow() ? 1 : 0) - this._cover) * Math.min(1, dt / 0.3);
            this._rainLayer.alpha = 1 - this._cover;
            this._rainLayer.visible = this._cover < 0.99;
        }
        // snowflakes: drifting with the wind, swaying, sliding against the camera's movement (the near ones more)
        if (this._flakes.length) {
            const last = this._lastOrigin ?? { x: originX, y: originY };
            const camDx = (originX - last.x) / unitsPerPx, camDy = (originY - last.y) / unitsPerPx;
            for (const flake of this._flakes) {
                const s = flake.sprite;
                const parallax = 0.85 + flake.depth * 0.45;
                s.x += (flake.vx + Math.cos(this._time * 1.3 + flake.phase) * flake.sway) * dt - camDx * parallax;
                s.y += flake.vy * dt - camDy * parallax;
                if (s.y > h + 20) { s.y -= h + 40; s.x = Math.random() * w; }
                if (s.y < -20) s.y += h + 40;
                if (s.x > w + 20) s.x -= w + 40;
                if (s.x < -20) s.x += w + 40;
            }
        }
        // rain: fall, drift with the wind, and slide against camera movement (parallax)
        if (this._streaks.length) {
            const last = this._lastOrigin ?? { x: originX, y: originY };
            const camDx = (originX - last.x) / unitsPerPx, camDy = (originY - last.y) / unitsPerPx;
            for (const streak of this._streaks) {
                const s = streak.sprite;
                s.x += streak.vx * dt - camDx * 1.12;
                s.y += streak.vy * dt - camDy * 1.12;
                if (s.y > h + 40) { s.y -= h + 80; s.x = Math.random() * w; }
                if (s.y < -40) s.y += h + 80;
                if (s.x > w + 40) s.x -= w + 80;
                if (s.x < -40) s.x += w + 80;
            }
            this._splashTimer += dt * this._splashRate;
            while (this._splashTimer >= 1) {
                this._splashTimer -= 1;
                const splash = this._splashes.find(s => s.age >= s.life);
                if (!splash) break;
                const x = originX + Math.random() * w * unitsPerPx, y = originY + Math.random() * h * unitsPerPx;
                // (none under a roof: they splashed on the floors of houses shown with their roofs off)
                if (roofOver({ x, y })) continue;
                // (in a thunderstorm, the drops on water open wide rings that spread slowly: 1.05)
                const onWater = this._storm && MapManager.terrain?.getFloor({ x, y }, 0) === FloorNames.Water;
                splash.size = onWater ? 2.4 + Math.random() * 1.2 : 1;
                splash.age = 0; splash.life = onWater ? 0.7 + Math.random() * 0.4 : 0.28 + Math.random() * 0.14;
                splash.shown = true;
                splash.sprite.position.set(x * PIXI_SCALE, y * PIXI_SCALE);
            }
            for (const splash of this._splashes) {
                if (splash.age >= splash.life) continue;
                splash.age += dt;
                const t = Math.min(1, splash.age / splash.life);
                const size = (0.25 + 0.75 * t) * 0.9 * splash.size * PIXI_SCALE / 64;
                splash.sprite.scale.set(size, size * 0.8);
                splash.sprite.alpha = splash.shown ? 0.5 * (1 - t) : 0;
                if (t >= 1) { splash.shown = false; splash.sprite.alpha = 0; }
            }
        }
        this._lastOrigin = { x: originX, y: originY };

        // footprints behind everyone walking on mud or silt at ground level
        if (this._printMax > 0 && MapManager.terrain) {
            const seen = new Set<number>();
            for (const player of Game.objects.getCategory(ObjectCategory.Player)) {
                seen.add(player.id);
                // (nobody in a plane or under a chute leaves footprints: 1.06)
                if (player.dead || player.layer !== Layer.Ground || player.air) { this._walkers.delete(player.id); continue; }
                const { x, y } = player.position;
                const walker = this._walkers.get(player.id);
                if (!walker) { this._walkers.set(player.id, { x, y, side: 1 }); continue; }
                const dx = x - walker.x, dy = y - walker.y;
                const dist = Math.hypot(dx, dy);
                if (dist > 20) { walker.x = x; walker.y = y; continue; } // teleported / respawned
                if (dist < 1.15) continue;
                const ux = dx / dist, uy = dy / dist;
                const steps = Math.min(8, Math.floor(dist / 1.15));
                for (let k = 1; k <= steps; k++) {
                    const px = walker.x + ux * 1.15 * k, py = walker.y + uy * 1.15 * k;
                    const floor = MapManager.terrain.getFloor({ x: px, y: py }, 0);
                    walker.side = -walker.side;
                    if (floor !== FloorNames.Grass && floor !== FloorNames.Sand) continue;
                    const print = this._prints.reduce((oldest, p) => (p.age > oldest.age ? p : oldest), this._prints[0]);
                    print.age = 0;
                    print.on = true;
                    print.sprite.position.set((px - uy * 0.22 * walker.side) * PIXI_SCALE, (py + ux * 0.22 * walker.side) * PIXI_SCALE);
                    print.sprite.rotation = Math.atan2(dy, dx) + Math.PI / 2;
                    print.sprite.scale.x = Math.abs(print.sprite.scale.x) * (walker.side > 0 ? 1 : -1);
                    print.sprite.alpha = floor === FloorNames.Sand ? this._printAlpha * 0.74 : this._printAlpha;
                }
                walker.x = x; walker.y = y;
            }
            for (const id of this._walkers.keys()) if (!seen.has(id)) this._walkers.delete(id);
            for (const print of this._prints) {
                if (!print.on) continue;
                print.age += dt;
                const t = print.age / this._printLife;
                if (t >= 1) { print.on = false; print.sprite.alpha = 0; continue; }
                if (t > 0.55) print.sprite.alpha = Math.min(print.sprite.alpha, this._printAlpha * (1 - (t - 0.55) / 0.45));
            }
            for (const print of this._bigPrints) {
                if (!print.on) continue;
                print.age += dt;
                const t = print.age / (this._printLife * 1.5);
                if (t >= 1) { print.on = false; print.sprite.alpha = 0; continue; }
                if (t > 0.6) print.sprite.alpha = Math.min(print.sprite.alpha, BIG_PRINT_ALPHA * (1 - (t - 0.6) / 0.4));
            }
        }
    }

    /**
     * a great print in the snow (0.98.4, the Yeti's: "his footsteps look really big and different from human
     * footsteps"): at `position`, the walker heading `heading` (radians), its left or right foot (`side` -1 or 1),
     * drawn deep and wide (`YetiPrints`: about three times a boot's size) and staying half as long again as a boot's.
     * Only where prints are drawn at all (the snow; on grass and sand), on the ground floor
     */
    bigPrint(position: { readonly x: number, readonly y: number }, heading: number, side: number): void {
        if (this._printMax <= 0 || !this._groundLayer || !MapManager.terrain) return;
        const floor = MapManager.terrain.getFloor(position, 0);
        if (floor !== FloorNames.Grass && floor !== FloorNames.Sand) return;
        let print = this._bigPrints.length < BIG_PRINT_MAX ? undefined : this._bigPrints.reduce((oldest, p) => (p.age > oldest.age ? p : oldest), this._bigPrints[0]);
        if (!print) {
            const sprite = new TrenchioSprite("yeti_print");
            sprite.anchor.set(0.5);
            sprite.scale.set((YetiPrints.length * PIXI_SCALE) / 88);
            this._groundLayer.addChild(sprite);
            this._bigPrints.push(print = { sprite, age: 99, on: false });
        }
        print.age = 0;
        print.on = true;
        const sprite = print.sprite;
        sprite.position.set(position.x * PIXI_SCALE, position.y * PIXI_SCALE);
        sprite.rotation = heading + Math.PI / 2;
        sprite.scale.x = Math.abs(sprite.scale.x) * (side > 0 ? 1 : -1);
        sprite.alpha = floor === FloorNames.Sand ? BIG_PRINT_ALPHA * 0.74 : BIG_PRINT_ALPHA;
    }

    /** the Yeti's prints shown now (tests and the lab) */
    get bigPrintCount(): number { return this._bigPrints.filter(p => p.on).length; }
}

/** the Yeti's prints: how many stay at most, how dark they are */
const BIG_PRINT_MAX = 80;
const BIG_PRINT_ALPHA = 0.5;

export const WarWeather = new WarWeatherClass();
